// src/hooks/useConfig.ts - ОБНОВЛЕННАЯ ВЕРСИЯ с пагинацией
import { useState, useEffect } from 'react';
import { message } from 'antd';
import { configApi } from '@/services/api/config';
import { MenuCategory, MenuItem, QRCodeStats } from '@/types';
import { useSite } from './useSite';

interface PaginationInfo {
  total: number;
  page: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
}

export const useConfig = () => {
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [qrStats, setQrStats] = useState<QRCodeStats | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const { siteConfig } = useSite(); // Получаем настройки сайта

  // ========== ЗАГРУЗКА ДАННЫХ ==========

  // Загрузка категорий
  const loadCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await configApi.getCategories();
      setCategories(response.data.data);
    } catch (error: any) {
      message.error('Ошибка загрузки категорий');
      console.error('Error loading categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Загрузка блюд с фильтрацией
  const loadMenuItems = async (params?: { 
    category_id?: number; 
    category_ids?: number[];
    search?: string;
    status?: string;
    page?: number;
    per_page?: number;
  }) => {
    setItemsLoading(true);
    try {
      // ИСПРАВЛЕНО: Увеличиваем per_page чтобы получить все данные
      const requestParams = {
        ...params,
        per_page: params?.per_page || 100 // Загружаем больше данных по умолчанию
      };
      
      const response = await configApi.getMenuItems(requestParams);
      
      // ИСПРАВЛЕНО: Сохраняем и данные и пагинацию
      setMenuItems(response.data.data.items);
      setPagination(response.data.data.pagination);
      
    } catch (error: any) {
      message.error('Ошибка загрузки блюд');
      console.error('Error loading menu items:', error);
    } finally {
      setItemsLoading(false);
    }
  };

  // Загрузка статистики QR-кодов
  const loadQrStats = async () => {
    try {
      const response = await configApi.getQrCodeStats();
      setQrStats(response.data.data);
    } catch (error: any) {
      console.error('Error loading QR stats:', error);
    }
  };

  // ========== ОПЕРАЦИИ С КАТЕГОРИЯМИ ==========

  // Создание категории
  const createCategory = async (data: Partial<MenuCategory>) => {
    setLoading(true);
    try {
      const response = await configApi.createCategory(data);
      message.success('Категория создана');
      await loadCategories();
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка создания категории';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Обновление категории
  const updateCategory = async (categoryId: number, data: Partial<MenuCategory>) => {
    setLoading(true);
    try {
      const response = await configApi.updateCategory(categoryId, data);
      message.success('Категория обновлена');
      await loadCategories();
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка обновления категории';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Удаление категории
  const deleteCategory = async (categoryId: number) => {
    setLoading(true);
    try {
      await configApi.deleteCategory(categoryId);
      message.success('Категория удалена');
      await loadCategories();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка удаления категории';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // ========== ОПЕРАЦИИ С БЛЮДАМИ ==========

  // Создание блюда
  const createMenuItem = async (formData: FormData) => {
    setLoading(true);
    try {
      const response = await configApi.createMenuItem(formData);
      message.success('Блюдо создано');
      await loadMenuItems();
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка создания блюда';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Обновление блюда
  const updateMenuItem = async (itemId: number, data: any) => {
    setLoading(true);
    try {
      console.log('🔄 updateMenuItem called with:', data);
      console.log('🖼️ Has image:', data.image instanceof File);
      console.log('📷 Has additional images:', data.additional_images?.length || 0);
      console.log('🗑️ Images to remove:', data.images_to_remove?.length || 0);
      
      let response;
      
      // Проверяем есть ли изображения или операции с изображениями
      // В методе updateMenuItem найти и заменить эту часть:

      const hasImageOperations = 
        data.image instanceof File || 
        (data.additional_images && data.additional_images.length > 0) ||
        (data.images_to_remove && data.images_to_remove.length > 0);

      // ИСПРАВЛЕНИЕ: Добавить дополнительную отладку
      console.log('🔍 Image operations check:', {
        hasImage: data.image instanceof File,
        hasAdditionalImages: !!(data.additional_images && data.additional_images.length > 0),
        hasImagesToRemove: !!(data.images_to_remove && data.images_to_remove.length > 0),
        additionalImagesLength: data.additional_images?.length || 0,
        imagesToRemoveLength: data.images_to_remove?.length || 0,
        willUseFormData: hasImageOperations
      });

      if (hasImageOperations) {
        console.log('📦 Using FormData method');
        const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (key === 'category' && Array.isArray(value)) {
          console.log('🏷️ Processing categories:', value);
          value.forEach(catId => {
            console.log(`Adding category: ${catId} (type: ${typeof catId})`);
            formData.append('category', catId.toString());
          });
        } else if (key === 'additional_images' && Array.isArray(value)) {
          console.log('📷 Processing additional images:', value.length);
          value.forEach((file, index) => {
            console.log(`Adding additional image ${index + 1}: ${file.name} (${file.size} bytes)`);
            formData.append('additional_images', file);
          });
        } else if (key === 'images_to_remove' && Array.isArray(value)) {
          console.log('🗑️ Processing images to remove:', value);
          value.forEach(index => {
            formData.append('images_to_remove', index.toString());
          });
        } else if (value instanceof File) {
          formData.append(key, value);
        } else if (value !== null && value !== undefined) {
          formData.append(key, value.toString());
        }
      });

      // Показать содержимое FormData
      console.log('📋 FormData entries:');
      for (let [key, val] of formData.entries()) {
        if (val instanceof File) {
          console.log(`  ${key}: File(${val.name})`);
        } else {
          console.log(`  ${key}: ${val}`);
        }
      }

      response = await configApi.updateMenuItem(itemId, formData);
      } else {
            console.log('🔢 Using JSON method');
      const jsonData = {
        ...data,
        category: Array.isArray(data.category) ? data.category : [data.category]
      };

      console.log('📋 Final JSON data:', jsonData);

      response = await configApi.updateMenuItemData(itemId, jsonData); 
      }      
      
      message.success('Блюдо обновлено');
      await loadMenuItems();
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка обновления блюда';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Удаление блюда
  const deleteMenuItem = async (itemId: number) => {
    setLoading(true);
    try {
      await configApi.deleteMenuItem(itemId);
      message.success('Блюдо удалено');
      await loadMenuItems();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка удаления блюда';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // ========== ОПЕРАЦИИ СО СКИДКАМИ ==========

  // Создание скидки
  const createDiscount = async (itemId: number, discountPercent: number) => {
    setLoading(true);
    try {
      await configApi.createDiscount(itemId, discountPercent);
      message.success(`Скидка ${discountPercent}% создана`);
      await loadMenuItems();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка создания скидки';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Удаление скидки
  const removeDiscount = async (itemId: number) => {
    setLoading(true);
    try {
      await configApi.removeDiscount(itemId);
      message.success('Скидка удалена');
      await loadMenuItems();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка удаления скидки';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // ========== ОПЕРАЦИИ С QR-КОДАМИ ==========

  // Генерация QR-кода
  const generateQrCode = async (itemId: number) => {
    setLoading(true);
    try {
      const response = await configApi.generateQrCode(itemId);
      message.success('QR-код успешно сгенерирован');
      await loadMenuItems();
      await loadQrStats();
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка генерации QR-кода';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Удаление QR-кода
  const removeQrCode = async (itemId: number) => {
    setLoading(true);
    try {
      await configApi.removeQrCode(itemId);
      message.success('QR-код удален');
      await loadMenuItems();
      await loadQrStats();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка удаления QR-кода';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Массовая генерация QR-кодов
  const bulkGenerateQrCodes = async (itemIds: number[]) => {
    setLoading(true);
    try {
      const response = await configApi.bulkGenerateQrCodes(itemIds);
      const result = response.data.data;
      
      if (result.failed_count > 0) {
        message.warning(
          `QR-коды: ${result.success_count} успешно, ${result.failed_count} с ошибками`
        );
      } else {
        message.success(`${result.success_count} QR-кодов успешно сгенерировано`);
      }
      
      await loadMenuItems();
      await loadQrStats();
      return result;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка массовой генерации QR-кодов';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const bulkDownloadQrCodes = async (itemIds: number[]) => {
    setLoading(true);
    try {
      const response = await configApi.bulkDownloadQrCodes(itemIds);
      
      // Логика скачивания файла из Blob-ответа
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'menu_qr_codes.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Очистка памяти

      message.success('QR-коды успешно скачаны');
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка скачивания QR-кодов';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // ========== ДОПОЛНИТЕЛЬНЫЕ МЕТОДЫ ==========

  // Получение детальной информации о блюде
  const getMenuItemDetails = async (itemId: number) => {
    try {
      const response = await configApi.getMenuItem(itemId);
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка загрузки деталей блюда';
      message.error(errorMsg);
      throw error;
    }
  };

  // Получение статистики каталога
  const getConfigStats = async () => {
    try {
      const response = await configApi.getConfigStats();
      return response.data.data;
    } catch (error: any) {
      console.error('Error loading config stats:', error);
      return null;
    }
  };

  // ========== УТИЛИТЫ ==========

  // Построение иерархического списка категорий
  const getCategoriesHierarchy = () => {
    const buildHierarchy = (parentId: number | null = null): MenuCategory[] => {
      return categories
        .filter(category => category.parent === parentId)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(category => ({
          ...category,
          children: buildHierarchy(category.id)
        }));
    };

    return buildHierarchy();
  };

  // Получение всех подкategорий для категории
  const getSubcategories = (categoryId: number): MenuCategory[] => {
    const subcategories: MenuCategory[] = [];
    
    const findSubcategories = (parentId: number) => {
      categories.forEach(category => {
        if (category.parent === parentId) {
          subcategories.push(category);
          findSubcategories(category.id);
        }
      });
    };

    findSubcategories(categoryId);
    return subcategories;
  };

  // Фильтр блюд по нескольким критериям
  const getFilteredItems = (filters: {
    categoryIds?: number[];
    status?: string;
    hasDiscount?: boolean;
    hasQrCode?: boolean;
    searchTerm?: string;
  }) => {
    return menuItems.filter(item => {
      // Фильтр по категориям
      if (filters.categoryIds && filters.categoryIds.length > 0) {
        const hasCategory = filters.categoryIds.some(catId => 
          item.category.includes(catId)
        );
        if (!hasCategory) return false;
      }

      // Фильтр по статусу
      if (filters.status && item.status !== filters.status) {
        return false;
      }

      // Фильтр по наличию скидки
      if (filters.hasDiscount !== undefined) {
        if (filters.hasDiscount && !item.has_discount) return false;
        if (!filters.hasDiscount && item.has_discount) return false;
      }

      // Фильтр по наличию QR-кода
      if (filters.hasQrCode !== undefined) {
        if (filters.hasQrCode && !item.qr_code) return false;
        if (!filters.hasQrCode && item.qr_code) return false;
      }

      // Поиск по названию
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const nameMatch = item.name.toLowerCase().includes(searchLower) ||
                         (item.name_kz && item.name_kz.toLowerCase().includes(searchLower));
        const articleMatch = item.article.toLowerCase().includes(searchLower);
        if (!nameMatch && !articleMatch) return false;
      }

      return true;
    });
  };

  // Загрузка при монтировании
  useEffect(() => {
    loadCategories();
    loadMenuItems();
    loadQrStats();
  }, []);

  // Обновление блюда через JSON (без изображений)
  const updateMenuItemData = async (itemId: number, data: any) => {
    setLoading(true);
    try {
      console.log('🔢 updateMenuItemData called with:', data);
      
      const response = await configApi.updateMenuItemData(itemId, data);
      
      message.success('Блюдо обновлено');
      await loadMenuItems();
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Ошибка обновления блюда';
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    // ========== ДАННЫЕ ==========
    categories,
    menuItems,
    qrStats,
    pagination, // ДОБАВЛЕНО
    siteConfig,
    
    // ========== СОСТОЯНИЕ ЗАГРУЗКИ ==========
    loading,
    categoriesLoading,
    itemsLoading,
    
    // ========== МЕТОДЫ ЗАГРУЗКИ ==========
    loadCategories,
    loadMenuItems,
    loadQrStats,
    
    // ========== ОПЕРАЦИИ С КАТЕГОРИЯМИ ==========
    createCategory,
    updateCategory,
    deleteCategory,
    
    // ========== ОПЕРАЦИИ С БЛЮДАМИ ==========
    createMenuItem,
    updateMenuItem,
    updateMenuItemData,
    deleteMenuItem,
    getMenuItemDetails,
    
    // ========== ОПЕРАЦИИ СО СКИДКАМИ ==========
    createDiscount,
    removeDiscount,
    
    // ========== ОПЕРАЦИИ С QR-КОДАМИ ==========
    generateQrCode,
    removeQrCode,
    bulkGenerateQrCodes,
    bulkDownloadQrCodes,
    
    // ========== ДОПОЛНИТЕЛЬНЫЕ МЕТОДЫ ==========
    getConfigStats,
    getCategoriesHierarchy,
    getSubcategories,
    getFilteredItems,
  };
};